// pages/MapLoi.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup, Annotation } from 'react-simple-maps';
import { Scale, BookOpen, Calendar, Baby, Heart, Users, Clock, ChevronRight, MapPin, Loader2, Globe } from 'lucide-react';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function MapLoi() {
  const [selectedPays, setSelectedPays] = useState(null);
  const [legalData, setLegalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipPays, setTooltipPays] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [paysAvecLois, setPaysAvecLois] = useState([]);

  // Charger la liste des pays qui ont des lois
  useEffect(() => {
    const fetchPaysAvecLois = async () => {
      try {
        const res = await axios.get(`${API_URL}/legal-rules`);
        if (res.data.success) {
          setPaysAvecLois(res.data.pays || []);
        }
      } catch (err) {
        // Silencieux
      }
    };
    fetchPaysAvecLois();
  }, []);

  const handlePaysClick = async (geoId, geoName) => {
    // Trouver le pays dans la liste des pays avec lois
    const paysAvecLoi = paysAvecLois.find(p => p.Nom === geoName);
    
    if (!paysAvecLoi) {
      // Pays sans loi → afficher quand même mais avec message
      setSelectedPays({ geoId, nom: geoName, aDesLois: false });
      setLegalData(null);
      return;
    }

    setSelectedPays({ ...paysAvecLoi, aDesLois: true });
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/legal-rules/${paysAvecLoi.Code}`);
      if (res.data.success) {
        setLegalData(res.data.rule);
      } else {
        setLegalData(null);
      }
    } catch (err) {
      setLegalData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  // Vérifier si un pays a des lois
  const paysALois = (geoName) => {
    return paysAvecLois.some(p => p.Nom === geoName);
  };

  const getPaysInfo = (geoName) => {
    return paysAvecLois.find(p => p.Nom === geoName);
  };

  // Noms complets des pays (la lib utilise les noms anglais)
  const countryNames = {
    'Morocco': 'Maroc',
    'France': 'France',
    'Senegal': 'Sénégal',
    "Cote d'Ivoire": "Côte d'Ivoire",
    'Tunisia': 'Tunisie',
    'Algeria': 'Algérie',
    'Spain': 'Espagne',
    'Portugal': 'Portugal',
    'Italy': 'Italie',
    'Germany': 'Allemagne',
    'Belgium': 'Belgique',
    'Netherlands': 'Pays-Bas',
    'Mauritania': 'Mauritanie',
    'Mali': 'Mali',
    'Niger': 'Niger',
    'Burkina Faso': 'Burkina Faso',
    'Guinea': 'Guinée',
    'Ghana': 'Ghana',
    'Nigeria': 'Nigéria',
    'Cameroon': 'Cameroun',
    'Egypt': 'Égypte',
    'Libya': 'Libye',
    'Sudan': 'Soudan',
    'Chad': 'Tchad',
    // Ajouter d'autres selon besoin
  };

  const getFrenchName = (geoName) => {
    return countryNames[geoName] || geoName;
  };

  return (
    <div className="flex h-full gap-0" onMouseMove={handleMouseMove}>
      
      {/* CARTE */}
      <div className="flex-1 relative bg-gradient-to-br from-slate-900 to-slate-800">
        {/* En-tête carte */}
        <div className="absolute top-4 left-4 z-10">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Globe className="text-emerald-400" size={22} />
            Législations par pays
          </h1>
          <p className="text-slate-300 text-sm mt-1">Cliquez sur un pays pour consulter ses lois</p>
        </div>

        {/* Badge pays avec lois */}
        <div className="absolute top-4 right-4 z-10 bg-slate-800/80 backdrop-blur rounded-lg px-3 py-2 text-white text-xs flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          {paysAvecLois.length} législation{paysAvecLois.length > 1 ? 's' : ''} disponibl{paysAvecLois.length > 1 ? 'es' : 'e'}
        </div>

        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{
            scale: 180,
            center: [0, 25]
          }}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup zoom={1} minZoom={1} maxZoom={4}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const nomFr = getFrenchName(geo.properties.name);
                  const aDesLois = paysALois(nomFr);
                  const estSelectionne = selectedPays?.nom === nomFr || selectedPays?.Nom === nomFr;
                  
                  return (
                    <g key={geo.rsmKey}>
                      <Geography
                        geography={geo}
                        onClick={() => handlePaysClick(geo.id, nomFr)}
                        onMouseEnter={() => {
                          setTooltipPays({ nom: nomFr, aDesLois });
                        }}
                        onMouseLeave={() => setTooltipPays(null)}
                        style={{
                          default: {
                            fill: estSelectionne 
                              ? '#10b981' 
                              : aDesLois 
                                ? '#064e3b' 
                                : '#1e293b',
                            stroke: estSelectionne ? '#fbbf24' : aDesLois ? '#34d399' : '#334155',
                            strokeWidth: estSelectionne ? 1.5 : 0.5,
                            outline: 'none',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          },
                          hover: {
                            fill: aDesLois ? '#059669' : '#334155',
                            stroke: aDesLois ? '#6ee7b7' : '#475569',
                            strokeWidth: 1,
                            outline: 'none',
                            cursor: 'pointer',
                          },
                          pressed: {
                            fill: '#34d399',
                            outline: 'none',
                          },
                        }}
                      />
                    </g>
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Tooltip */}
        {tooltipPays && (
          <div
            className="fixed z-50 bg-slate-800 text-white px-3 py-2 rounded-lg shadow-xl pointer-events-none text-sm"
            style={{
              left: mousePos.x + 15,
              top: mousePos.y - 40,
            }}
          >
            <span className="font-semibold">{tooltipPays.nom}</span>
            {tooltipPays.aDesLois ? (
              <span className="text-emerald-400 ml-2">✓ Loi disponible</span>
            ) : (
              <span className="text-slate-400 ml-2">En attente</span>
            )}
          </div>
        )}

        {/* Légende */}
        <div className="absolute bottom-4 left-4 flex gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-emerald-600 border border-emerald-400" />
            <span className="text-slate-300">Législation disponible</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-slate-700" />
            <span className="text-slate-400">En attente de traitement</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-emerald-400 ring-1 ring-yellow-400" />
            <span className="text-slate-300">Sélectionné</span>
          </div>
        </div>
      </div>

      {/* PANNEAU DROIT - DÉTAIL LOIS */}
      <div className="w-[420px] bg-white border-l border-gray-200 overflow-y-auto flex flex-col">
        
        {!selectedPays && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <Globe size={48} className="text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Sélectionnez un pays</h3>
            <p className="text-sm text-gray-400">
              Cliquez sur un pays de la carte pour afficher sa législation du travail
            </p>
          </div>
        )}

        {loading && (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <Loader2 size={32} className="text-emerald-500 animate-spin mb-3" />
            <p className="text-sm text-gray-500">Chargement de la législation...</p>
          </div>
        )}

        {selectedPays && selectedPays.aDesLois && legalData && !loading && (
          <div className="p-5">
            {/* En-tête pays */}
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <span className="text-3xl">
                {selectedPays.Code === 'MA' ? '🇲🇦' : 
                 selectedPays.Code === 'FR' ? '🇫🇷' : 
                 selectedPays.Code === 'SN' ? '🇸🇳' : 
                 selectedPays.Code === 'CI' ? '🇨🇮' : 
                 selectedPays.Code === 'TN' ? '🇹🇳' : 
                 selectedPays.Code === 'DZ' ? '🇩🇿' : '🌍'}
              </span>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{selectedPays.Nom || selectedPays.nom}</h2>
                <p className="text-xs text-gray-500">{legalData.codeTravail}</p>
              </div>
              <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                v{legalData.versionNumber}
              </span>
            </div>

            {/* Source */}
            <div className="mb-5 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold mb-1">
                <BookOpen size={14} />
                Source officielle
              </div>
              <p className="text-xs text-amber-600">{legalData.config.source}</p>
            </div>

            {/* Congé annuel */}
            <SectionCard icon={<Calendar size={18} />} title="Congé annuel" color="emerald">
              <InfoLigne label="Taux acquisition (majeur)" value={`${legalData.config.parametres.conge_annuel.taux_acquisition_majeur} j/mois`} />
              <InfoLigne label="Taux acquisition (mineur)" value={`${legalData.config.parametres.conge_annuel.taux_acquisition_mineur} j/mois`} />
              <InfoLigne label="Plafond" value={`${legalData.config.parametres.conge_annuel.plafond_jours} jours`} />
              <InfoLigne label="Ancienneté minimale" value={`${legalData.config.parametres.conge_annuel.anciennete_minimale_mois} mois`} />
              <InfoLigne label="Majoration / 5 ans" value={`${legalData.config.parametres.conge_annuel.majoration_anciennete_par_5_ans} j`} />
              <InfoLigne label="Fractionnement min" value={`${legalData.config.parametres.conge_annuel.fractionnement_minimum_jours} jours`} />
              <InfoLigne label="Cumul max" value={`${legalData.config.parametres.conge_annuel.cumul_max_annees} année(s)`} />
            </SectionCard>

            {/* Congés spéciaux */}
            <SectionCard icon={<Heart size={18} />} title="Congés spéciaux" color="rose">
              <InfoLigne label="Naissance" value={`${legalData.config.parametres.conges_speciaux.naissance_jours} jours`} />
              <InfoLigne label="Mariage" value={`${legalData.config.parametres.conges_speciaux.mariage_salarie_jours} jours`} />
              <InfoLigne label="Décès conjoint" value={`${legalData.config.parametres.conges_speciaux.deces_conjoint_jours} jours`} />
              <InfoLigne label="Décès frère/sœur" value={`${legalData.config.parametres.conges_speciaux.deces_frere_soeur_jours} jours`} />
              <InfoLigne label="Circoncision" value={`${legalData.config.parametres.conges_speciaux.circoncision_jours} jours`} />
            </SectionCard>

            {/* Weekend */}
            <SectionCard icon={<Clock size={18} />} title="Weekend" color="blue">
              <InfoLigne label="Samedi travaillé" value={legalData.config.parametres.weekend.samedi_travaille ? 'Oui' : 'Non'} />
              <InfoLigne label="Dimanche travaillé" value={legalData.config.parametres.weekend.dimanche_travaille ? 'Oui' : 'Non'} />
            </SectionCard>

            {/* Jours fériés */}
            <SectionCard icon={<Users size={18} />} title="Jours fériés" color="purple">
              <InfoLigne label="Prolongent le congé" value={legalData.config.parametres.feries.prolongent_conge ? 'Oui' : 'Non'} />
            </SectionCard>

            {/* Articles de référence */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-600 text-xs font-semibold mb-2">
                <Scale size={14} />
                Articles de référence
              </div>
              <div className="flex flex-wrap gap-1.5">
                {legalData.config.articles_reference.map((article, i) => (
                  <span key={i} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded font-mono">
                    Art. {article}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Pays sans loi */}
        {selectedPays && !selectedPays.aDesLois && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <Globe size={48} className="text-amber-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {selectedPays.nom || selectedPays.Nom}
            </h3>
            <p className="text-sm text-gray-400 mb-1">
              Législation non encore traitée
            </p>
            <p className="text-xs text-gray-300">
              Notre équipe travaille à l'intégration de ce pays
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Sous-composant Section
function SectionCard({ icon, title, color, children }) {
  const colorMap = {
    emerald: 'border-emerald-200 bg-emerald-50/50',
    rose: 'border-rose-200 bg-rose-50/50',
    blue: 'border-blue-200 bg-blue-50/50',
    purple: 'border-purple-200 bg-purple-50/50',
  };
  
  const titleColorMap = {
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
  };

  return (
    <div className={`mb-3 p-3 rounded-lg border ${colorMap[color] || ''}`}>
      <div className={`flex items-center gap-2 text-sm font-semibold mb-2 ${titleColorMap[color] || ''}`}>
        {icon}
        {title}
      </div>
      <div className="space-y-1.5">
        {children}
      </div>
    </div>
  );
}

// Sous-composant Ligne d'info
function InfoLigne({ label, value }) {
  return (
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-700">{value}</span>
    </div>
  );
}